import { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Menu, 
  X, 
  Home, 
  User,
  Users, 
  Calendar, 
  Palette, 
  Settings, 
  LogOut,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Zap,
  UserPlus,
  List,
  FileText,
  Mail,
  MessageSquare,
  DollarSign,
  Package,
  Shield,
  Image,
  BarChart3,
  Bell,
  Star,
  MapPin,
  CalendarDays,
  Settings as SettingsIcon,
  History,
  UserX,
  UserMinus,
  Building2,
  Plus,
  Sparkles,
  Activity,
  BookOpen,
  Loader2,
  ClipboardList,
  FlaskConical,
  Play,
  PenLine,
  Layers,
  Search,
  Route,
} from 'lucide-react';
import { RootState } from '../store';
import { logout } from '../store/slices/authSlice';
import { setLanguage } from '@digitaltattoo/redux-slices';
import { HelpIcon } from './HelpIcon';
import { getDocPathFromNavId } from '../config/documentation';
import { notificationsApi } from '../services/notificationsApi';
import { useGlobalLoadingState } from '../services/globalLoading';
import { CurrentShopProvider } from '../contexts/CurrentShopContext';
import { CurrentShopSelector } from './CurrentShopSelector';
import { ScreenSaverLock } from './ScreenSaverLock';
import type { RbacPermission } from '@digitaltattoo/types';
import { useRbac } from '../contexts/RbacContext';

interface NavigationItem {
  id: string;
  label: string;
  icon: React.ComponentType<any>;
  path?: string;
  children?: NavigationItem[];
  docPath?: string;
  dividerBefore?: string;
  /** If set, the item is shown only to these normalized roles (e.g. super_admin). */
  roles?: string[];
  excludedRoles?: string[];
  permission?: RbacPermission;
}

function isNavItemVisible(
  item: NavigationItem,
  role: string,
  can: (permission: RbacPermission) => boolean,
): boolean {
  if (item.excludedRoles?.includes(role)) return false;
  if (item.permission && !can(item.permission)) return false;

  if (!item.roles || item.roles.length === 0) return true;

  return item.roles.includes(role);
}

function filterNavigationByRole(
  items: NavigationItem[],
  role: string,
  can: (permission: RbacPermission) => boolean,
): NavigationItem[] {
  return items.flatMap((item) => {
    if (!isNavItemVisible(item, role, can)) return [];
    const children = item.children ? filterNavigationByRole(item.children, role, can) : undefined;
    if (item.children && (!children || children.length === 0) && !item.path) {
      return [];
    }
    return [{ ...item, children }];
  });
}

function normalizeMenuText(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase();
}

interface FlattenedNavHit {
  item: NavigationItem;
  ancestors: NavigationItem[];
}

function flattenNav(
  items: NavigationItem[],
  ancestors: NavigationItem[] = [],
): FlattenedNavHit[] {
  const out: FlattenedNavHit[] = [];
  for (const item of items) {
    if (item.path) {
      out.push({ item, ancestors });
    }
    if (item.children?.length) {
      out.push(...flattenNav(item.children, [...ancestors, item]));
    }
  }
  return out;
}

function navItemMatches(query: string, item: NavigationItem, ancestors: NavigationItem[]): boolean {
  const tokens = normalizeMenuText(query).split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return true;
  const haystack = normalizeMenuText(
    [...ancestors.map((a) => a.label), item.dividerBefore || '', item.label].join(' '),
  );
  return tokens.every((token) => haystack.includes(token));
}

export function Layout() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state: RootState) => state.auth);
  const isAdminUser = Boolean((user as any)?.role);
  const isPractitionerUser = (user as any)?.userType === 'PRACTITIONER';
  const isEmployeeUser = (user as any)?.userType === 'EMPLOYEE';
  const role = String((user as any)?.role || '').toLowerCase().replace(/-/g, '_');
  const isSuperAdmin = role === 'super_admin';
  const { can } = useRbac();
  const currentLanguage = useSelector((state: RootState) => state.language.current);
  const showHelp = useSelector((state: RootState) => state.preferences.showHelp);
  const { isLoading: isGlobalLoading, message: globalLoadingMessage } = useGlobalLoadingState();
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const [expandedSections, setExpandedSections] = useState<string[]>(['agenda']);
  const [isTestMode, setIsTestMode] = useState(false);
  const [operationMode, setOperationMode] = useState<'FULL' | 'NO_DELETE' | 'READ_ONLY'>('FULL');
  const [menuQuery, setMenuQuery] = useState('');
  /*
   * Acteur ayant déverrouillé la session avec son code staff.
   * Les identités métier sont conservées : les pages (congés, audit, …)
   * en ont besoin pour rattacher une action à la bonne personne.
   */
  const [unlockedBy, setUnlockedBy] = useState<{
    id?: string;
    name: string;
    role?: string;
    type?: string;
    practitionerId?: string;
    employeeId?: string;
    shopId?: string;
  } | null>(null);
  useEffect(() => {
    fetch('/api/system/status', { cache: 'no-store' })
      .then((r) => r.json())
      .then((d) => {
        const mode = d.data?.operationMode;
        setOperationMode(mode === 'NO_DELETE' || mode === 'READ_ONLY' ? mode : 'FULL');
      })
      .catch(() => setOperationMode('FULL'));
  }, [location.pathname]);

  useEffect(() => {
    const onModeChanged = (event: Event) => {
      const mode = (event as CustomEvent<{ mode?: string }>).detail?.mode;
      setOperationMode(mode === 'NO_DELETE' || mode === 'READ_ONLY' ? mode : 'FULL');
    };
    window.addEventListener('app:operation-mode-changed', onModeChanged);
    return () => window.removeEventListener('app:operation-mode-changed', onModeChanged);
  }, []);

  // Fetch test mode setting once on mount — admin-only endpoint, skip for practitioners/employees
  useEffect(() => {
    if (isPractitionerUser || isEmployeeUser) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch('/api/admin/settings/general', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.success && typeof d.data?.isTestMode === 'boolean') {
          setIsTestMode(d.data.isTestMode);
        }
      })
      .catch(() => {});
  }, [isPractitionerUser]);

  // Auto-expand relevant section based on current route (exclusive accordion)
  useEffect(() => {
    const path = location.pathname;
    const autoExpandMap: Record<string, string> = {
      '/admin/': 'admin',
      '/settings/': 'admin',
      '/documents/': 'admin',
      '/planning/': 'agenda',
      '/appointments': 'agenda',
      '/tasks': 'tasks',
      '/clients': 'clients',
      '/practitioners': 'team',
      '/employees': 'team',
      '/vendors': 'team',
      '/services': 'catalogue',
      '/body-zones': 'catalogue',
      '/piercing-types': 'catalogue',
      '/item-collections': 'catalogue',
      '/inventory': 'catalogue',
      // '/financial', '/reports', '/media', '/safety' — sections not yet implemented
      '/companies': 'platform',
      '/shops': isSuperAdmin ? 'platform' : 'shops',
      '/admins': 'platform',
      '/users': 'platform',
    };
    for (const [prefix, sectionId] of Object.entries(autoExpandMap)) {
      if (path.startsWith(prefix)) {
        setExpandedSections([sectionId]);
        break;
      }
    }
  }, [location.pathname, isSuperAdmin]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const mainContentRef = useRef<HTMLDivElement>(null);
  const sidebarNavRef = useRef<HTMLElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);

  const loadNotifications = async (_opts?: { force?: boolean }) => {
    setNotifLoading(true);
    try {
      const feed = await notificationsApi.inboxFeed({ limit: 20 });
      setNotifications(feed.data ?? []);
      setUnreadCount(feed.unreadCount ?? 0);
    } catch (e) {
      console.warn('Failed to load notifications:', e);
    } finally {
      setNotifLoading(false);
    }
  };

  // Scroll main content to top when route changes (but NOT the sidebar)
  useEffect(() => {
    if (mainContentRef.current) {
      mainContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
    // Explicitly preserve sidebar scroll position by not touching sidebarNavRef
  }, [location.pathname]);

  useEffect(() => {
    const handleToggle = () => setSidebarVisible(prev => !prev);
    const handleSet = (event: Event) => {
      const customEvent = event as CustomEvent<{ visible?: boolean }>;
      if (typeof customEvent.detail?.visible === 'boolean') {
        setSidebarVisible(customEvent.detail.visible);
      }
    };

    window.addEventListener('appSidebar:toggle', handleToggle);
    window.addEventListener('appSidebar:set', handleSet);
    return () => {
      window.removeEventListener('appSidebar:toggle', handleToggle);
      window.removeEventListener('appSidebar:set', handleSet);
    };
  }, []);

  // Close notifications dropdown on outside click
  useEffect(() => {
    const onClick = (ev: MouseEvent) => {
      if (!notifOpen) return;
      const el = notifRef.current;
      if (!el) return;
      if (ev.target instanceof Node && !el.contains(ev.target)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [notifOpen]);

  // Close user profile dropdown on outside click
  useEffect(() => {
    const onClick = (ev: MouseEvent) => {
      if (!profileOpen) return;
      const el = profileRef.current;
      if (!el) return;
      if (ev.target instanceof Node && !el.contains(ev.target)) {
        setProfileOpen(false);
      }
    };

    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [profileOpen]);

  // Poll notifications when authenticated
  useEffect(() => {
    if (!user) return;
    loadNotifications();
    const id = window.setInterval(() => loadNotifications(), 15000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const handleLanguageChange = (lang: 'en' | 'fr') => {
    i18n.changeLanguage(lang);
    dispatch(setLanguage(lang));
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const toggleSection = (sectionId: string) => {
    // Exclusive accordion: open one section at a time (closing the previous)
    setExpandedSections((prev) => (prev.includes(sectionId) ? [] : [sectionId]));
  };

  // ── 1. Dashboard ──────────────────────────────────────────────
  const dashboardItem: NavigationItem = {
    id: 'dashboard',
    label: t('navigation.dashboard'),
    icon: Home,
    path: '/dashboard',
    docPath: getDocPathFromNavId('dashboard') || undefined
  };

  // ── 2. Agenda (Calendar + Appointments + Schedule) ────────────
  const agendaItem: NavigationItem = {
    id: 'agenda',
    label: t('navigation.agenda.title'),
    icon: Calendar,
    docPath: getDocPathFromNavId('planning') || undefined,
    children: [
      {
        id: 'calendar',
        label: t('navigation.calendar'),
        icon: Calendar,
        path: '/planning/calendar',
        docPath: '08-gestion-planning#08-2-calendrier',
        permission: 'planning:read:shop',
      },
      {
        id: 'appointments-list',
        label: t('navigation.appointments.list'),
        icon: List,
        path: '/appointments',
        docPath: '07-gestion-rendez-vous#07-2-liste',
        permission: 'planning:read:shop',
      },
      {
        id: 'appointments-create',
        label: t('navigation.appointments.create'),
        icon: UserPlus,
        path: '/appointments/create',
        docPath: '07-gestion-rendez-vous#07-3-creation',
        permission: 'appointments:create',
      },
      {
        id: 'appointments-create-anonymous',
        label: t('navigation.appointments.createAnonymous'),
        icon: UserMinus,
        path: '/appointments/create-anonymous',
        permission: 'appointments:create',
      },
      {
        id: 'planning-rules',
        label: t('navigation.planning.rules'),
        icon: SettingsIcon,
        path: '/planning/rules',
        dividerBefore: t('navigation.planning.title'),
        docPath: '08-gestion-planning#08-3-regles',
        permission: 'schedule:configure:shop',
      },
      {
        id: 'planning-events',
        label: t('navigation.planning.events'),
        icon: CalendarDays,
        path: '/planning/events',
        docPath: '08-gestion-planning#08-2-calendrier',
        permission: 'planning:write:shop',
      },
      {
        id: 'planning-timeoffs',
        label: t('navigation.planning.timeoffs'),
        icon: UserX,
        path: '/planning/timeoffs',
        docPath: '08-gestion-planning#08-4-conges',
        permission: 'time-off:manage:shop',
      },
      {
        id: 'planning-audit',
        label: t('navigation.planning.audit'),
        icon: History,
        path: '/planning/audit',
        docPath: '08-gestion-planning#08-5-audit',
        permission: 'logs:read',
      },
      {
        id: 'planning-acts',
        label: t('navigation.planning.acts'),
        icon: FileText,
        path: '/planning/acts',
        dividerBefore: t('navigation.planning.acts'),
        docPath: '08-gestion-planning#08-7-activites',
        excludedRoles: ['manager'],
        permission: 'activities:manage:shop',
      }
    ]
  };

  // ── 2b. Tasks ─────────────────────────────────────────────────
  const tasksItem: NavigationItem = {
    id: 'tasks',
    label: 'Tâches',
    icon: ClipboardList,
    docPath: '17-gestion-taches',
    children: [
      {
        id: 'tasks-list',
        label: 'Liste des tâches',
        icon: List,
        path: '/tasks',
        docPath: '17-gestion-taches#17-2-liste',
      },
      {
        id: 'tasks-create',
        label: 'Nouvelle tâche',
        icon: Plus,
        path: '/tasks/create',
        docPath: '17-gestion-taches#17-3-creation',
      },
    ],
  };

  // ── 2c. Tests (only shown when isTestMode=true) ───────────────
  const testsItem: NavigationItem = {
    id: 'tests',
    label: 'Tests',
    icon: FlaskConical,
    children: [
      {
        id: 'test-cases-list',
        label: 'Cas de test',
        icon: FlaskConical,
        path: '/test-cases',
      },
      {
        id: 'test-cases-create',
        label: 'Nouveau cas de test',
        icon: Plus,
        path: '/test-cases/create',
      },
      {
        id: 'test-case-instances-list',
        label: 'Exécutions',
        icon: Play,
        path: '/test-case-instances',
      },
    ],
  };

  // ── 3. Clients ────────────────────────────────────────────────
  const clientsItem: NavigationItem = {
    id: 'clients',
    label: t('navigation.clients.title'),
    icon: Users,
    docPath: getDocPathFromNavId('clients') || undefined,
    children: [
      {
        id: 'clients-list',
        label: t('navigation.clients.list'),
        icon: List,
        path: '/clients',
        docPath: '05-gestion-clients#05-2-liste'
      },
      {
        id: 'clients-create',
        label: t('navigation.clients.create'),
        icon: UserPlus,
        path: '/quick-client-create',
        docPath: '05-gestion-clients#05-3-creation-rapide'
      },
      {
        id: 'clients-create-full',
        label: t('navigation.clients.createFull'),
        icon: FileText,
        path: '/clients/create-full',
        docPath: '05-gestion-clients#05-4-creation-complete'
      }
    ]
  };

  // ── 4. Team (Practitioners + Employees + Vendors) ─────────────
  const teamItem: NavigationItem = {
    id: 'team',
    label: t('navigation.team.title'),
    icon: Palette,
    children: [
      {
        id: 'practitioners-list',
        label: t('navigation.practitioners.list'),
        icon: Palette,
        path: '/practitioners',
        docPath: '06-gestion-praticiens#06-2-liste'
      },
      {
        id: 'practitioners-create',
        label: t('navigation.practitioners.create'),
        icon: UserPlus,
        path: '/practitioners/create',
        docPath: '06-gestion-praticiens#06-3-creation'
      },
      ...(isAdminUser
        ? [{
            id: 'practitioners-applications',
            label: 'Demandes partenaires',
            icon: FileText,
            path: '/practitioners/applications',
          } as NavigationItem]
        : []),
      {
        id: 'employees-list',
        label: t('navigation.employees.list'),
        icon: Users,
        path: '/employees',
        dividerBefore: t('navigation.employees.title'),
        docPath: 'employees#liste'
      },
      {
        id: 'employees-create',
        label: t('navigation.employees.create'),
        icon: Plus,
        path: '/employees/create',
        docPath: 'employees#creation'
      },
      {
        id: 'vendors-list',
        label: t('navigation.vendors.list'),
        icon: Package,
        path: '/vendors',
        dividerBefore: t('navigation.vendors.title'),
        docPath: 'vendors#liste'
      },
      {
        id: 'vendors-create',
        label: t('navigation.vendors.create'),
        icon: Plus,
        path: '/vendors/create',
        docPath: 'vendors#creation'
      }
    ]
  };

  // ── 5. Catalogue (Services + Activities + Piercing + Inventory)
  const catalogueItem: NavigationItem = {
    id: 'catalogue',
    label: t('navigation.catalogue.title'),
    icon: Sparkles,
    children: [
      {
        id: 'services-list',
        label: t('navigation.services.list'),
        icon: List,
        path: '/services',
        docPath: 'services#liste'
      },
      {
        id: 'services-create',
        label: t('navigation.services.create'),
        icon: Plus,
        path: '/services/create',
        docPath: 'services#creation'
      },
      // services-pricing: not yet implemented — hidden
      {
        id: 'planning-activity-types',
        label: t('navigation.planning.activityTypes'),
        icon: Activity,
        path: '/planning/activity-types',
        dividerBefore: t('navigation.planning.activities'),
        docPath: '08-gestion-planning#08-6-types-activites'
      },
      {
        id: 'planning-employee-activity-types',
        label: t('navigation.planning.employeeActivityTypes'),
        icon: Activity,
        path: '/planning/employee-activity-types',
        docPath: '08-gestion-planning#08-6-types-activites'
      },
      {
        id: 'planning-activities',
        label: t('navigation.planning.activities'),
        icon: Activity,
        path: '/planning/activities',
        docPath: '08-gestion-planning#08-7-activites'
      },
      {
        id: 'item-collections',
        label: t('navigation.piercing.itemCollections'),
        icon: Layers,
        path: '/item-collections',
        dividerBefore: t('navigation.piercing.title'),
        docPath: '10-donnees-reference#10-1-zones-corps'
      },
      {
        id: 'body-zones',
        label: t('navigation.piercing.bodyZones'),
        icon: MapPin,
        path: '/body-zones',
        docPath: '10-donnees-reference#10-1-zones-corps'
      },
      {
        id: 'piercing-types',
        label: t('navigation.piercing.types'),
        icon: Star,
        path: '/piercing-types',
        docPath: '10-donnees-reference#10-2-types-piercing'
      },
      // inventory-products, inventory-jewelry: not yet implemented — hidden
    ]
  };

  // ── 6. Finance & Reports ──────────────────────────────────────
  const financeReportsItem: NavigationItem = {
    id: 'financeReports',
    label: t('navigation.financeReports.title'),
    icon: DollarSign,
    children: [
      {
        id: 'financial-invoices',
        label: t('navigation.financial.invoices'),
        icon: FileText,
        path: '/financial/invoices'
      },
      {
        id: 'financial-payments',
        label: t('navigation.financial.payments'),
        icon: DollarSign,
        path: '/financial/payments'
      },
      {
        id: 'reports-business',
        label: t('navigation.reports.business'),
        icon: BarChart3,
        path: '/reports/business',
        dividerBefore: t('navigation.reports.title')
      },
      {
        id: 'reports-clients',
        label: t('navigation.reports.clients'),
        icon: Users,
        path: '/reports/clients'
      }
    ]
  };

  // ── 7. Content & Compliance (Media + Safety) ──────────────────
  const contentComplianceItem: NavigationItem = {
    id: 'contentCompliance',
    label: t('navigation.contentCompliance.title'),
    icon: Image,
    children: [
      {
        id: 'media-gallery',
        label: t('navigation.media.gallery'),
        icon: Image,
        path: '/media/gallery'
      },
      {
        id: 'media-qr',
        label: t('navigation.media.qr'),
        icon: Image,
        path: '/media/qr'
      },
      {
        id: 'safety-protocols',
        label: t('navigation.safety.protocols'),
        icon: Shield,
        path: '/safety/protocols',
        dividerBefore: t('navigation.safety.title')
      },
      {
        id: 'safety-health',
        label: t('navigation.safety.health'),
        icon: Shield,
        path: '/safety/health'
      }
    ]
  };

  // ── 8. Administration / Settings ──────────────────────────────
  const adminItem: NavigationItem = isAdminUser
    ? {
        id: 'admin',
        label: t('navigation.admin.title'),
        icon: Settings,
        docPath: '16-parametres',
        children: [
          {
            id: 'settings-preferences',
            label: t('navigation.settings.preferences'),
            icon: Settings,
            path: '/settings/preferences',
            docPath: '16-parametres#16-2-1-préférences'
          },
          {
            id: 'settings-general',
            label: t('navigation.settings.general'),
            icon: Settings,
            path: '/settings/general',
            docPath: '16-parametres#16-2-paramètres-généraux',
            permission: 'platform:settings',
          },
          {
            id: 'settings-rbac',
            label: 'Rôles et permissions',
            icon: Shield,
            path: '/settings/rbac',
            permission: 'platform:rbac:write',
          },
          {
            id: 'settings-profile',
            label: t('navigation.settings.profile'),
            icon: User,
            path: '/settings/profile',
            docPath: '16-parametres#16-3-mon-profil'
          },
          {
            id: 'settings-password',
            label: t('navigation.settings.password'),
            icon: Shield,
            path: '/settings/password',
            docPath: '16-parametres#16-4-changer-le-mot-de-passe'
          },
          {
            id: 'admin-email-logs',
            label: t('navigation.settings.emailLogs'),
            icon: Mail,
            path: '/admin/email-logs',
            dividerBefore: 'Communications',
            permission: 'logs:read',
          },
          {
            id: 'admin-booking-journeys',
            label: t('navigation.settings.bookingJourneys'),
            icon: Route,
            path: '/admin/booking-journeys',
            permission: 'logs:read',
          },
          {
            id: 'admin-activity-logs',
            label: t('navigation.settings.activityLogs'),
            icon: Activity,
            path: '/admin/activity-logs',
            docPath: '29-journal-activites-clients',
            permission: 'logs:read',
          },
          {
            id: 'settings-email-templates',
            label: t('navigation.settings.emailTemplates'),
            icon: Mail,
            path: '/settings/email-templates'
          },
          {
            id: 'settings-sms',
            label: t('navigation.settings.smsSettings'),
            icon: MessageSquare,
            path: '/settings/sms',
            permission: 'platform:settings',
          },
          {
            id: 'admin-sms-logs',
            label: t('navigation.settings.smsLogs'),
            icon: MessageSquare,
            path: '/admin/sms-logs',
            permission: 'logs:read',
          },
          {
            id: 'settings-templates',
            label: t('navigation.settings.templates'),
            icon: FileText,
            path: '/documents/templates',
            dividerBefore: 'Documents'
          },
          {
            id: 'settings-client-uploads',
            label: t('navigation.settings.clientUploads'),
            icon: FileText,
            path: '/documents/client-uploads'
          },
          {
            id: 'settings-contracts',
            label: t('navigation.settings.contracts'),
            icon: FileText,
            path: '/documents/contracts'
          },
        ]
      }
    : {
        id: 'settings',
        label: t('navigation.settings.title'),
        icon: Settings,
        docPath: '16-parametres',
        children: [
          {
            id: 'settings-preferences',
            label: t('navigation.settings.preferences'),
            icon: Settings,
            path: '/settings/preferences',
            docPath: '16-parametres#16-2-1-préférences'
          },
          {
            id: 'settings-profile',
            label: t('navigation.settings.profile'),
            icon: User,
            path: '/settings/profile',
            docPath: '16-parametres#16-3-mon-profil'
          },
          {
            id: 'settings-password',
            label: t('navigation.settings.password'),
            icon: Shield,
            path: '/settings/password',
            docPath: '16-parametres#16-4-changer-le-mot-de-passe'
          },
        ]
      };

  // ── 9. Help ───────────────────────────────────────────────────
  const helpItem: NavigationItem = {
    id: 'help',
    label: t('navigation.help'),
    icon: BookOpen,
    path: '/docs/manual/fr/index.html',
  };

  // ── 10. Platform (super_admin only) ───────────────────────────
  const platformItem: NavigationItem = {
    id: 'platform',
    label: t('navigation.platform.title'),
    icon: Building2,
    children: [
      {
        id: 'companies-list',
        label: t('navigation.companies.list'),
        icon: Building2,
        path: '/companies',
        docPath: '03-entreprises#03-2-liste',
        permission: 'platform:companies:write',
      },
      {
        id: 'companies-create',
        label: t('navigation.companies.create'),
        icon: Plus,
        path: '/companies/create',
        docPath: '03-entreprises#03-3-creation',
        permission: 'platform:companies:write',
      },
      {
        id: 'shops-list',
        label: t('navigation.shops.list'),
        icon: Package,
        path: '/shops',
        dividerBefore: t('navigation.shops.title'),
        docPath: '04-boutiques#04-2-liste'
      },
      {
        id: 'shops-create',
        label: t('navigation.shops.create'),
        icon: UserPlus,
        path: '/shops/create',
        docPath: '04-boutiques#04-3-creation',
        permission: 'platform:shops:create',
      },
      {
        id: 'admins-list',
        label: t('navigation.admins.list'),
        icon: Shield,
        path: '/admins',
        dividerBefore: t('navigation.admins.title'),
        docPath: '09-gestion-administrateurs#09-2-liste',
        permission: 'platform:admins:manage',
      },
      {
        id: 'admins-create',
        label: t('navigation.admins.create'),
        icon: UserPlus,
        path: '/admins/create',
        docPath: '09-gestion-administrateurs#09-3-creation',
        permission: 'platform:admins:manage',
      },
      {
        id: 'users-list',
        label: 'Liste des utilisateurs',
        icon: User,
        path: '/users',
        dividerBefore: 'Utilisateurs',
        permission: 'platform:users:manage',
      },
      {
        id: 'users-create',
        label: 'Créer un utilisateur',
        icon: UserPlus,
        path: '/users/create',
        permission: 'platform:users:manage',
      }
    ]
  };

  // Regular admin gets Shops list only (creation is super_admin)
  const regularAdminShopsItem: NavigationItem = {
    id: 'shops',
    label: t('navigation.shops.title'),
    icon: Package,
    docPath: getDocPathFromNavId('shops') || undefined,
    children: [
      {
        id: 'shops-list',
        label: t('navigation.shops.list'),
        icon: List,
        path: '/shops',
        docPath: '04-boutiques#04-2-liste'
      },
    ]
  };

  // Practitioner area (minimal navigation)
  const practitionerNavigationItems: NavigationItem[] = [
    {
      id: 'practitioner-dashboard',
      label: 'Tableau de Bord',
      icon: Home,
      path: '/practitioner/dashboard',
    },
    {
      id: 'practitioner-appointments',
      label: 'Mes rendez-vous',
      icon: CalendarDays,
      children: [
        {
          id: 'practitioner-appointments-list',
          label: 'Liste',
          icon: List,
          path: '/practitioner/appointments',
        },
        {
          id: 'practitioner-appointments-create',
          label: 'Créer',
          icon: UserPlus,
          path: '/practitioner/appointments/create',
        },
      ],
    },
    {
      id: 'practitioner-profile',
      label: 'Mon profil',
      icon: PenLine,
      path: '/practitioner/profile',
    },
    {
      id: 'help',
      label: t('navigation.help'),
      icon: BookOpen,
      path: '/docs/manual/fr/index.html',
      docPath: undefined,
    },
  ];

  // Combine navigation items based on user type/role
  // financeReportsItem and contentComplianceItem are not yet implemented — hidden from nav
  const arrivalItem: NavigationItem = {
    id: 'arrival',
    label: 'Accueil Client',
    icon: UserPlus,
    path: '/arrival',
    docPath: '30-accueil-client-reception',
  };

  const employeeNavigationItems: NavigationItem[] = [
    { id: 'employee-dashboard', label: 'Réception', icon: Home, path: '/employee/dashboard' },
  ];

  const managerNavigationItems: NavigationItem[] = [
    dashboardItem,
    { ...arrivalItem, label: t('navigation.home') },
    agendaItem,
    clientsItem,
  ];

  const navigationItems: NavigationItem[] = filterNavigationByRole(
    isPractitionerUser
      ? practitionerNavigationItems
      : isEmployeeUser
      ? employeeNavigationItems
      : role === 'manager'
      ? managerNavigationItems
      : [
          dashboardItem,
          arrivalItem,
          agendaItem,
          tasksItem,
          ...(isTestMode ? [testsItem] : []),
          clientsItem,
          teamItem,
          catalogueItem,
          // financeReportsItem,    — not yet implemented
          // contentComplianceItem, — not yet implemented
          ...(isSuperAdmin ||
          can('platform:companies:write') ||
          can('platform:shops:create') ||
          can('platform:admins:manage') ||
          can('platform:users:manage')
            ? [platformItem]
            : [regularAdminShopsItem]),
          adminItem,
          helpItem,
        ],
    role,
    can,
  );

    const isActiveItem = (item: NavigationItem): boolean => {
    if (item.path) {
      return location.pathname === item.path;
    }
    if (item.children) {
      return item.children.some(child => location.pathname === child.path);
    }
    return false;
  };

  const renderNavigationItem = (item: NavigationItem, level = 0, subtitle?: string) => {
    const isActive = isActiveItem(item);
    const isExpanded = expandedSections.includes(item.id);
    const hasChildren = item.children && item.children.length > 0;

    const handleButtonClick = (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('.help-icon-container')) {
        return;
      }
      
      if (hasChildren) {
        toggleSection(item.id);
      } else if (item.path) {
        if (item.path.includes('/docs/')) {
          window.open(item.path, '_blank');
        } else {
          navigate(item.path);
          setSidebarOpen(false);
        }
        setMenuQuery('');
      }
    };

    const divider = item.dividerBefore ? (
      <div className="flex items-center gap-2 ml-4 mt-2 mb-1 px-4">
        {item.dividerBefore.trim() && (
          <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 whitespace-nowrap">
            {item.dividerBefore}
          </span>
        )}
        <div className="flex-1 border-t border-gray-200" />
      </div>
    ) : null;

    return (
      <div key={item.id}>
        {divider}
        <div className="group">
          <div className="flex shrink-0 items-center">
            <button
              onClick={handleButtonClick}
              className={`flex-1 flex items-center justify-between px-4 py-3 text-left rounded-lg transition-colors duration-200 ${
                isActive
                  ? 'bg-primary-100 text-primary-700 font-medium'
                  : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
              } ${level > 0 ? 'ml-4 text-sm' : ''}`}
            >
              <div className="flex items-center space-x-3 min-w-0">
                <item.icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-primary-600' : 'text-gray-500'}`} />
                <span className="min-w-0">
                  <span className="block truncate">{item.label}</span>
                  {subtitle && subtitle !== item.label ? (
                    <span className="block truncate text-[10px] font-normal text-gray-400">{subtitle}</span>
                  ) : null}
                </span>
              </div>
              {hasChildren && (
                <div className="flex items-center">
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  )}
                </div>
              )}
            </button>
            
            {item.docPath && showHelp && (
              <div 
                className="help-icon-container opacity-0 group-hover:opacity-100 transition-opacity pr-2"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <HelpIcon 
                  docPath={item.docPath} 
                  tooltip={`Documentation : ${item.label}`}
                  size="sm"
                />
              </div>
            )}
          </div>
          
          {hasChildren && isExpanded && (
            <div className="mt-1 space-y-1">
              {item.children!.map(child => renderNavigationItem(child, level + 1))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <CurrentShopProvider>
    <div className="h-[100dvh] min-h-0 max-h-[100dvh] bg-gray-50 flex overflow-hidden">
      {isGlobalLoading && (
        <div className="fixed inset-0 z-[60] bg-white/50 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-white border border-gray-200 rounded-lg px-4 py-3 shadow-sm flex items-center gap-2 text-sm text-gray-700 max-w-sm mx-4">
            <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
            <span>{globalLoadingMessage ?? t('common.loading')}</span>
          </div>
        </div>
      )}
      {/* Sidebar */}
      <div className={`fixed left-0 z-50 w-64 shrink-0 max-w-[min(100%,16rem)] bg-white shadow-lg transform flex flex-col overflow-hidden h-[100dvh] max-h-[100dvh] min-h-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } transition-transform duration-300 ease-in-out lg:static lg:h-[100dvh] lg:max-h-[100dvh] ${
        sidebarVisible ? 'lg:translate-x-0' : 'lg:hidden'
      }`}>
        
        {/* Sidebar Header */}
        <div className="flex items-center justify-between min-h-16 pt-[env(safe-area-inset-top,0px)] px-3 lg:px-6 border-b border-gray-200 flex-shrink-0 box-border">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-br from-primary-300 to-accent-500 rounded-lg">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-display font-semibold text-textPrimary">
                Digital Tattoo
              </h1>
              <p className="text-xs text-textSecondary -mt-1">
                {isPractitionerUser
                  ? t('navigation.panelSubtitle.practitioner')
                  : isEmployeeUser
                    ? t('navigation.panelSubtitle.employee')
                    : t('navigation.panelSubtitle.admin')}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-1">
            <button
              onClick={() => setSidebarVisible(false)}
              className="hidden lg:inline-flex p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              title="Hide menu"
              aria-label="Hide menu"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              aria-label="Close menu"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="px-4 py-3 border-b border-gray-200 flex-shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
            <input
              type="text"
              value={menuQuery}
              onChange={(e) => setMenuQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setMenuQuery('');
              }}
              placeholder={t('navigation.searchPlaceholder')}
              aria-label={t('navigation.searchPlaceholder')}
              className="input-field pl-10 pr-8 text-sm"
            />
            {menuQuery ? (
              <button
                type="button"
                onClick={() => setMenuQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-gray-400 hover:text-gray-600"
                aria-label={t('navigation.searchClear')}
              >
                <X className="w-4 h-4" />
              </button>
            ) : null}
          </div>
        </div>

        {/* Navigation */}
        <nav ref={sidebarNavRef} className="flex-1 min-h-0 px-4 py-6 space-y-2 overflow-y-auto overflow-x-hidden overscroll-contain touch-pan-y">
          {menuQuery.trim()
            ? (() => {
                const hits = flattenNav(navigationItems).filter(({ item, ancestors }) =>
                  navItemMatches(menuQuery, item, ancestors),
                );
                if (hits.length === 0) {
                  return (
                    <p className="px-4 py-2 text-sm text-gray-500">{t('navigation.searchNoResults')}</p>
                  );
                }
                return hits.map(({ item, ancestors }) => {
                  const subtitle =
                    ancestors.length > 0
                      ? ancestors[ancestors.length - 1].label
                      : item.dividerBefore;
                  return renderNavigationItem(
                    { ...item, dividerBefore: undefined, children: undefined },
                    0,
                    subtitle,
                  );
                });
              })()
            : navigationItems.map((item) => renderNavigationItem(item))}
        </nav>

        <div className="shrink-0 border-t border-gray-200 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] lg:hidden">
          <button
            type="button"
            onClick={() => {
              setSidebarOpen(false);
              handleLogout();
            }}
            className="flex min-h-11 w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-red-600 hover:bg-red-50"
          >
            <LogOut className="h-5 w-5 shrink-0" />
            <span>{t('navigation.logout')}</span>
          </button>
        </div>
      </div>
      {!sidebarVisible && (
        <button
          type="button"
          onClick={() => setSidebarVisible(true)}
          className="hidden lg:flex fixed top-4 left-2 z-50 items-center justify-center w-8 h-8 rounded-full bg-white shadow border border-gray-200 text-gray-600 hover:text-gray-800 hover:bg-gray-50"
          title="Show menu"
          aria-label="Show menu"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col lg:ml-0 min-w-0 min-h-0 overflow-hidden" aria-busy={isGlobalLoading}>
        {/* Header */}
        <header className="relative z-20 flex-shrink-0 bg-white shadow-sm border-b border-gray-200 min-h-16 pt-[env(safe-area-inset-top,0px)] flex min-w-0 items-center justify-between gap-2 px-2 sm:px-6 box-border">
          <div className="flex shrink-0 items-center">
            <button
              onClick={() => setSidebarOpen(true)}
              aria-label="Ouvrir le menu"
              aria-expanded={sidebarOpen}
              className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>

          <div className="flex min-w-0 flex-1 items-center justify-end gap-1 sm:gap-2 lg:flex-initial lg:gap-4">
            {isSuperAdmin && <CurrentShopSelector />}

            {/* Help */}
            <a
              href="/docs/manual/fr/index.html"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden lg:inline-flex p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
              title="Manuel utilisateur"
              aria-label="Aide"
            >
              <BookOpen className="w-5 h-5" />
            </a>

            {/* Notifications */}
            <div className="shrink-0 lg:relative" ref={notifRef}>
              <button
                onClick={() => {
                  setNotifOpen((v) => !v);
                  if (!notifOpen) {
                    loadNotifications({ force: true });
                  }
                }}
                aria-expanded={notifOpen}
                className="relative inline-flex h-11 w-11 items-center justify-center lg:h-auto lg:w-auto p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                aria-label="Notifications"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[11px] bg-red-600 text-white rounded-full">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>

              {notifOpen && (
                <div className="absolute inset-x-2 top-full mt-2 lg:inset-x-auto lg:top-auto lg:right-0 lg:w-96 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden z-50">
                  <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Notifications</p>
                      <p className="text-xs text-gray-500">{unreadCount} non lue(s)</p>
                    </div>
                    <button
                      onClick={() =>
                        notificationsApi
                          .markAll('read')
                          .then(() => loadNotifications({ force: true }))
                          .catch((e) => console.warn('Mark all failed:', e))
                      }
                      className="text-xs px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50"
                    >
                      Tout marquer lu
                    </button>
                  </div>

                  <div className="max-h-[min(24rem,60dvh)] overflow-auto">
                    {notifLoading ? (
                      <div className="px-4 py-6 text-sm text-gray-500">Chargement…</div>
                    ) : notifications.length === 0 ? (
                      <div className="px-4 py-6 text-sm text-gray-500">Aucune notification</div>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {notifications.map((n: any) => (
                          <div key={n.id} className={`px-4 py-3 ${n.read ? 'bg-white' : 'bg-primary-50'}`}>
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{n.title}</p>
                                {n.body && <p className="text-xs text-gray-600 mt-1 whitespace-pre-wrap break-words">{n.body}</p>}
                                {n.createdAt && (
                                  <p className="text-[11px] text-gray-400 mt-1">
                                    {new Date(n.createdAt).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                  </p>
                                )}
                              </div>
                              {!n.read && (
                                <button
                                  onClick={() =>
                                    notificationsApi
                                      .mark(n.id, 'read')
                                      .then(() => loadNotifications({ force: true }))
                                      .catch((e) => console.warn('Mark read failed:', e))
                                  }
                                  className="text-xs px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 flex-shrink-0"
                                >
                                  Lu
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Language Switcher */}
            <div className="hidden lg:flex shrink-0 items-center space-x-1 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => handleLanguageChange('en')}
                className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                  currentLanguage === 'en'
                    ? 'bg-white text-primary-500 shadow-sm'
                    : 'text-textSecondary hover:text-textPrimary'
                }`}
              >
                EN
              </button>
              <button
                onClick={() => handleLanguageChange('fr')}
                className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                  currentLanguage === 'fr'
                    ? 'bg-white text-primary-500 shadow-sm'
                    : 'text-textSecondary hover:text-textPrimary'
                }`}
              >
                FR
              </button>
            </div>

            {/* User Profile Dropdown */}
            <div className="relative shrink-0" ref={profileRef}>
              <button
                type="button"
                onClick={() => setProfileOpen((open) => !open)}
                className="flex min-h-11 min-w-11 items-center justify-center gap-2 rounded-lg p-1 lg:px-2 lg:py-1.5 text-left hover:bg-gray-100 transition-colors"
                aria-haspopup="menu"
                aria-expanded={profileOpen}
                aria-label="Menu utilisateur"
              >
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                  <span className="text-sm font-medium text-primary-600">
                    {(unlockedBy?.name || user?.name)?.charAt(0)?.toUpperCase() || 'A'}
                  </span>
                </div>

                <div className="hidden lg:block min-w-0 max-w-56">
                  {unlockedBy ? (
                    <>
                      <p className="truncate text-xs text-gray-500">
                        Connecté en tant que
                      </p>
                      <p className="truncate text-sm font-medium text-gray-900">
                        {unlockedBy.name}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="truncate text-sm font-medium text-gray-900">
                        {user?.name || 'Admin User'}
                      </p>
                      <p className="truncate text-xs text-gray-500">
                        {user?.email || 'admin@example.com'}
                      </p>
                    </>
                  )}
                </div>

                <ChevronDown
                  className={`hidden lg:block h-4 w-4 text-gray-400 transition-transform ${
                    profileOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {profileOpen && (
                <div
                  className="absolute right-0 mt-2 w-72 max-w-[calc(100vw-1rem)] max-h-[70dvh] overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg z-50"
                  role="menu"
                >
                  <div className="border-b border-gray-100 px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                        <span className="text-sm font-medium text-primary-600">
                          {user?.name?.charAt(0)?.toUpperCase() || 'A'}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="truncate text-sm font-semibold text-gray-900">
                            {user?.name || 'Admin User'}
                          </p>
                          {user?.role === 'super_admin' && (
                            <span className="inline-flex flex-shrink-0 items-center rounded bg-yellow-100 px-2 py-0.5 text-[10px] font-medium text-yellow-800">
                              Super Admin
                            </span>
                          )}
                        </div>
                        <p className="truncate text-xs text-gray-500">
                          {user?.email || 'admin@example.com'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-2">
                    <div className="lg:hidden">
                      <a
                        href="/docs/manual/fr/index.html"
                        target="_blank"
                        rel="noopener noreferrer"
                        role="menuitem"
                        className="flex min-h-11 items-center gap-3 rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <BookOpen className="h-4 w-4" />
                        <span>Manuel utilisateur</span>
                      </a>
                      <div className="flex gap-2 px-3 py-2" role="group" aria-label="Langue / Language">
                        {(['en', 'fr'] as const).map((language) => (
                          <button
                            key={language}
                            type="button"
                            role="menuitemradio"
                            aria-checked={currentLanguage === language}
                            onClick={() => handleLanguageChange(language)}
                            className={`min-h-11 flex-1 rounded-lg text-sm font-medium ${
                              currentLanguage === language
                                ? 'bg-primary-50 text-primary-600'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {language.toUpperCase()}
                          </button>
                        ))}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setProfileOpen(false);
                        navigate(isPractitionerUser ? '/practitioner/profile' : '/settings/profile');
                      }}
                      className="flex min-h-11 w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                      role="menuitem"
                    >
                      <User className="h-4 w-4 text-gray-500" />
                      <span>Mon profil</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setProfileOpen(false);
                        handleLogout();
                      }}
                      className="flex min-h-11 w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      role="menuitem"
                    >
                      <LogOut className="h-4 w-4" />
                      <span>{t('navigation.logout')}</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Test mode banner */}
        {isTestMode && (
          <div className="bg-amber-500 text-white px-6 py-2 flex items-center gap-2 text-sm font-medium flex-shrink-0">
            <FlaskConical className="w-4 h-4 flex-shrink-0" />
            <span>Application en mode TEST — Les données créées sont des données de test.</span>
          </div>
        )}

        {operationMode !== 'FULL' && (
          <div
            className={`px-6 py-2 flex items-center gap-2 text-sm font-medium flex-shrink-0 ${
              operationMode === 'READ_ONLY'
                ? 'bg-red-600 text-white'
                : 'bg-orange-500 text-white'
            }`}
          >
            <Shield className="w-4 h-4 flex-shrink-0" />
            <span>
              {operationMode === 'READ_ONLY'
                ? 'Application en lecture seule — aucune modification n’est autorisée.'
                : isSuperAdmin
                  ? 'Mode NO_DELETE — les suppressions restent disponibles pour le Super Admin.'
                  : 'Mode NO_DELETE — les suppressions sont désactivées. Annulation et désactivation restent disponibles.'}
            </span>
          </div>
        )}

        {/* Page Content */}
        <main ref={mainContentRef} className="dt-page-content flex-1 overflow-auto p-3 sm:p-6 min-w-0 min-h-0">
          <Outlet context={{ unlockedBy }} />
        </main>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
  <ScreenSaverLock
    user={user}
    onUnlockedByChange={setUnlockedBy}
  />
    </div>
    </CurrentShopProvider>
  );
}